import { web } from "@/config/System";
import { Navigation } from "@/constants";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import { GoDot } from "react-icons/go";
import { Link, useLocation } from "react-router-dom";
import HamburgerMenu from "./HamburgerMenu";
import { ThemeCustomizer } from "./ThemeCustomizer";

export default function Sidebar({
  navigationMenus,
}: {
  navigationMenus: Navigation[];
}) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState<boolean>(
    localStorage.getItem("openSidebar") &&
      JSON.parse(localStorage.getItem("openSidebar") || ""),
  );
  const [openMenu, setOpenMenu] = useState<string>("");
  const [hoveredMenu, setHoveredMenu] = useState<string>("");

  useEffect(() => {
    navigationMenus.map((navigation) => {
      navigation.menus.map((menu) => {
        if (menu.path !== null || !menu.submenus) return menu;

        return menu?.submenus.map((submenu) => {
          if (submenu.path === pathname) setOpenMenu(menu.name);
        });
      });
    });
  }, [pathname, navigationMenus]);

  return (
    <div className="z-50 sticky top-0 md:flex hidden items-center justify-center h-screen">
      <motion.aside
        initial={{ width: open ? "17rem" : "4.25rem" }}
        animate={{ width: open ? "17rem" : "4.25rem" }}
        className={`p-3 relative h-[97vh] bg-sidebar overflow-auto border-r border-slate-200 dark:border-slate-800 transition-colors duration-300`}
      >
        <section className="flex items-center justify-between">
          {open && (
            <Link to={"/"}>
              <h1 className="text-xl font-bold uppercase italic text-slate-900 dark:text-white">
                {web.title}
              </h1>
            </Link>
          )}
          <div className="mt-3 ml-5">
            <HamburgerMenu
              active={open}
              onClick={() => {
                localStorage.setItem("openSidebar", JSON.stringify(!open));
                setOpen((open) => !open);
              }}
            />
          </div>
        </section>
        <br />
        <section>
          <ul className="flex flex-col select-none gap-y-5 whitespace-nowrap">
            {navigationMenus &&
              navigationMenus.map((navigation, index) => {
                return (
                  <li key={index}>
                    <h1
                      className={`mb-3 text-xs font-semibold text-slate-500 dark:text-slate-400 ${
                        open ? "visible" : "invisible pointer-events-none"
                      }`}
                    >
                      {navigation.title}
                    </h1>

                    <div className="flex flex-col gap-y-3">
                      {navigation.menus.map((menu) => {
                        const active =
                          menu.path && pathname.startsWith(menu.path);
                        const hasSubmenus = menu.path === null;
                        const activeMenu = openMenu === menu.name;
                        const activeHoveredMenu =
                          !open && menu.name === hoveredMenu;

                        if (hasSubmenus)
                          return (
                            <div
                              key={menu.name}
                              className={`rounded-xl cursor-pointer transition-colors duration-200 ${
                                activeMenu
                                  ? "bg-secondary dark:bg-slate-800"
                                  : "hover:bg-secondary dark:hover:bg-slate-800"
                              }`}
                            >
                              <div
                                className="flex items-center justify-between p-2 text-slate-700 dark:text-slate-200"
                                onClick={() =>
                                  setOpenMenu(
                                    menu.name === openMenu ? "" : menu.name,
                                  )
                                }
                                onMouseEnter={() => setHoveredMenu(menu.name)}
                                onMouseLeave={() => setHoveredMenu("")}
                              >
                                <div className="flex items-center gap-x-3">
                                  <div className="text-xl">{menu.icon}</div>
                                  <div
                                    className={`${
                                      activeHoveredMenu
                                        ? "px-2 py-1 rounded-md bg-white dark:bg-slate-800 text-primary dark:text-white font-semibold border border-primary dark:border-slate-600 shadow-md z-50 absolute left-14"
                                        : ""
                                    } duration-300`}
                                  >
                                    {(open || activeHoveredMenu) && menu.name}
                                  </div>
                                </div>
                                {open && (
                                  <FiChevronRight
                                    size={20}
                                    className={`duration-300 ${
                                      activeMenu ? "rotate-90" : "rotate-0"
                                    }`}
                                  />
                                )}
                              </div>

                              {activeMenu && (
                                <ul className="p-2 space-y-1">
                                  {menu.submenus &&
                                    menu.submenus.map((submenu) => {
                                      const activeSubmenu =
                                        submenu.path == pathname;
                                      const activeHoveredSubmenu =
                                        !open && submenu.name === hoveredMenu;

                                      return (
                                        <li
                                          key={submenu.name}
                                          className={`p-2 rounded-xl transition-colors duration-200 ${
                                            activeSubmenu
                                              ? "bg-primary text-primary-foreground"
                                              : "hover:bg-black/5 dark:hover:bg-white/10 text-slate-600 dark:text-slate-400"
                                          }`}
                                          onMouseEnter={() =>
                                            setHoveredMenu(submenu.name)
                                          }
                                          onMouseLeave={() =>
                                            setHoveredMenu("")
                                          }
                                        >
                                          <Link
                                            to={submenu.path}
                                            className="flex items-center gap-x-3"
                                          >
                                            <div>
                                              <GoDot />
                                            </div>

                                            <div
                                              className={`${
                                                activeHoveredSubmenu
                                                  ? "px-2 py-1 rounded-md bg-white dark:bg-slate-800 text-primary dark:text-white font-semibold border border-primary dark:border-slate-600 shadow-md z-50 absolute left-14"
                                                  : ""
                                              } duration-300`}
                                            >
                                              {(open || activeHoveredSubmenu) &&
                                                submenu.name}
                                            </div>
                                          </Link>
                                        </li>
                                      );
                                    })}
                                </ul>
                              )}
                            </div>
                          );
                        return (
                          <div
                            key={menu.name}
                            onMouseEnter={() => setHoveredMenu(menu.name)}
                            onMouseLeave={() => setHoveredMenu("")}
                          >
                            <Link
                              to={menu.path || "#"}
                              className={`p-2 rounded-xl flex items-center gap-x-3 transition-colors duration-200 ${
                                active
                                  ? "bg-primary text-primary-foreground"
                                  : "hover:bg-secondary dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
                              }`}
                            >
                              <div className="text-xl">{menu.icon}</div>
                              <div
                                className={`${
                                  activeHoveredMenu
                                    ? "px-2 py-1 rounded-md bg-white dark:bg-slate-800 text-primary dark:text-white font-semibold border border-primary dark:border-slate-600 shadow-md z-50 absolute left-14"
                                    : ""
                                } duration-300`}
                              >
                                {(open || activeHoveredMenu) && menu.name}
                              </div>
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  </li>
                );
              })}
          </ul>
        </section>
        <br />
        <ThemeCustomizer />
      </motion.aside>
    </div>
  );
}
